import Papa from 'papaparse';

/**
 * Convert CSV text to a File object
 * @param {string} csvText - The CSV text content
 * @param {string} filename - The filename (default: 'edited.csv')
 * @returns {File} The File object
 */
export const csvTextToFile = (csvText, filename = 'edited.csv') => {
  const blob = new Blob([csvText], { type: 'text/csv;charset=utf-8;' });
  return new File([blob], filename, { type: 'text/csv' });
};

/**
 * Convert CSV string to an array of objects
 * Expects first line to be the headers
 * Uses papaparse for RFC 4180 compliant parsing
 * @param {string} csvString - The CSV string content
 * @returns {{ rows: Array, headers: Array }} Object containing the parsed rows and headers in order
 */
export const csvStringToObjects = (csvString) => {
  // Handle empty CSV case
  if (!csvString || csvString.trim() === '') {
    return { rows: [], headers: [''] };
  }

  // Parse CSV using papaparse with header row
  const parseResult = Papa.parse(csvString, {
    header: true,
    skipEmptyLines: true,
    transformHeader: (header) => header.trim() // Trim whitespace from headers only
    // Note: We don't trim values to preserve original behavior (whitespace in values should be preserved)
  });

  // Extract headers from the first row if available
  let headers = [];
  if (parseResult.meta.fields && parseResult.meta.fields.length > 0) {
    headers = parseResult.meta.fields;
  } else if (parseResult.data.length > 0) {
    // Fallback: extract headers from first data row keys
    headers = Object.keys(parseResult.data[0]);
  }

  // Remove papaparse internal fields (like __parsed_extra) from data
  const cleanedData = parseResult.data.map((row) => {
    const cleanedRow = { ...row };
    // Remove papaparse internal fields
    delete cleanedRow.__parsed_extra;
    return cleanedRow;
  });

  // Handle case where CSV has headers but no data rows
  if (headers.length === 0 && csvString.includes(',')) {
    // Try to parse just the header row
    const headerLine = csvString.split('\n')[0];
    const headerParse = Papa.parse(headerLine, {
      header: false,
      skipEmptyLines: false
    });
    if (headerParse.data.length > 0 && headerParse.data[0].length > 0) {
      headers = headerParse.data[0].map((h) => String(h || '').trim());
    }
  }

  // If still no headers, return default
  if (headers.length === 0) {
    return { rows: [], headers: [''] };
  }

  return { rows: cleanedData, headers };
};

/**
 * Convert an array of objects to CSV string
 * Excludes 'id' field from the output
 * Uses papaparse for RFC 4180 compliant CSV generation
 * @param {Array} rows - The array of objects to convert
 * @param {Array} columnOrder - Optional array specifying the column order (preserves original CSV order)
 * @returns {string} The CSV string
 */
export const objectsToCsvString = (rows, columnOrder = null) => {
  if (rows.length === 0) return '';

  // Prepare data by excluding 'id' field and ordering columns
  let columnNames;

  if (columnOrder) {
    // Use the provided column order, excluding 'id'
    columnNames = columnOrder.filter((key) => key !== 'id');
    // Ensure all columns from the data are included (in case columnOrder is incomplete)
    const dataColumns = Object.keys(rows[0]).filter((key) => key !== 'id');
    const missingColumns = dataColumns.filter(
      (col) => !columnNames.includes(col)
    );
    columnNames = [...columnNames, ...missingColumns];
  } else {
    // Fallback to Object.keys if no order is provided
    columnNames = Object.keys(rows[0]).filter((key) => key !== 'id');
  }

  // Prepare rows with only the selected columns and without 'id'
  const preparedRows = rows.map((row) => {
    const preparedRow = {};
    columnNames.forEach((col) => {
      const value = row[col];
      // Convert to string, handling null/undefined
      preparedRow[col] = value == null ? '' : String(value);
    });
    return preparedRow;
  });

  // Use papaparse to generate CSV string with proper escaping
  const csvString = Papa.unparse(preparedRows, {
    columns: columnNames, // Specify column order
    header: true, // Include header row
    newline: '\n' // Use Unix-style line endings
  });

  return csvString;
};
